import type { GenericAbortSignal } from 'axios';
import type { newCancelTokenFn } from 'hooks/useCancelToken';
import type { TradeListParams, TradeListResponse } from './conversation';
import { tradeListV2 } from './tradeV2';
import type {
  AllowOfferV2,
  BuyerPaid,
  Cancel,
  DeliveryMethodType,
  ErpSection,
  ExternalType,
  Item,
  ItemComment,
  Package,
  Return,
  ReturnLine,
  ReturnType,
  SearchTradesV2Query,
  SellerIncome,
  Tag,
  TagType,
  TradeV2Order,
} from './tradeV2';

/**
 * 订单 V2 -> V1 视图模型适配层。
 *
 * Sider 下的订单组件全部按 V1 `Conversation.Trade.Vo` 的结构消费数据，
 * 这里把 V2 的 section 结构一次性摊平成 V1 形状，避免逐个组件改造。
 * 物流轨迹、eligibility 仍走原有独立接口，在 useTrade 中补齐。
 */

const SUPPORTED_PLATFORMS = new Set([
  'SHOPEE',
  'LAZADA',
  'TIKTOK',
  'TIKTOK_US',
  'ALIEXPRESS',
  'MERCADO',
  'KWAI',
  'JOLECT',
  // WA_BIZ / WA 不是平台，其订单最终落在上面这些平台，仍走 V2
  'WA_BIZ',
  'WA',
]);

/** 当前 V2 接口支持的平台（TOKOPEDIA / FACEBOOK 尚未接入，继续走 V1） */
export function isTradeV2Supported(platform?: string | null) {
  return Boolean(platform) && SUPPORTED_PLATFORMS.has(String(platform).toUpperCase());
}

type SearchParamsExtras = TradeListParams & {
  trackingNumber?: string;
  itemName?: string;
  /** TradeSearchBar 使用的字段名 */
  spuId?: string;
  /** TradeSearchBar 使用的字段名 */
  sku?: string;
  skuId?: string;
  sellerSku?: string;
  externalItemId?: string;
  externalSkuId?: string;
};

const toNumberValue = (value?: string | number | null) => {
  const result = Number(value);
  return Number.isFinite(result) ? result : 0;
};

const toStringValue = (value?: string | number | null) =>
  value === undefined || value === null ? '' : String(value);

const splitIds = (value?: string | number | null) => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  const ids = String(value)
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);

  return ids.length ? ids : undefined;
};

const intersects = (a?: string[], b?: string[]) => Boolean(a?.some((value) => b?.includes(value)));

const hasTag = (tags: Tag[] | undefined, type: TagType) =>
  Boolean(tags?.some((tag) => tag.type === type));

const getTagRef = (tags: Tag[] | undefined, type: TagType) =>
  tags?.find((tag) => tag.type === type)?.refId;

const PLATFORM_MAP: Partial<Record<ExternalType, General.Platform>> = {
  SHOPEE: 'shopee',
  LAZADA: 'lazada',
  TOKOPEDIA: 'tokopedia',
  TIKTOK: 'tiktok',
  ALIEXPRESS: 'aliexpress',
  TIKTOK_US: 'tiktok_us',
  MERCADO: 'mercado',
  KWAI: 'kwai',
  FACEBOOK: 'facebook',
  JOLECT: 'jolect',
};

const toV1Platform = (platform?: ExternalType, fallbackPlatform?: string): General.Platform => {
  if (platform && PLATFORM_MAP[platform]) {
    return PLATFORM_MAP[platform]!;
  }

  const fallback = String(fallbackPlatform ?? '').toLowerCase();
  const knownPlatforms = Object.values(PLATFORM_MAP);

  return (
    knownPlatforms.includes(fallback as General.Platform) ? fallback : 'shopee'
  ) as General.Platform;
};

const RETURN_TYPE_MAP: Record<ReturnType, Conversation.Trade.IReturnType> = {
  TYPE_UNSPECIFIED: 'RETURN_REFUND',
  RETURN_REFUND: 'RETURN_REFUND',
  ONLY_REFUND: 'ONLY_REFUND',
  EXCHANGE: 'CHANGE',
  REPLACEMENT: 'REPLACEMENT',
};

const toV1ReturnType = (type?: ReturnType) =>
  RETURN_TYPE_MAP[type ?? 'RETURN_REFUND'] ?? 'RETURN_REFUND';

const toV1FulfillmentFlag = (fulfilledBy?: DeliveryMethodType) => {
  switch (fulfilledBy) {
    case 'LOCAL_SELLER':
      return 'fulfilled_by_local_seller' as const;
    case 'SHOPEE':
      return 'fulfilled_by_shopee' as const;
    case 'CROSS_BORDER_SELLER':
      return 'fulfilled_by_cb_seller' as const;
    default:
      return undefined;
  }
};

const toV1CancelType = (cancelBy?: Cancel['cancelBy']) => {
  switch (cancelBy) {
    case 'BUYER':
      return 'buyer' as const;
    case 'SELLER':
      return 'seller' as const;
    case 'SYSTEM':
      return 'system' as const;
    default:
      return undefined;
  }
};

const mapReturnType = (type?: ReturnType) => toV1ReturnType(type);

const mapAllowOffer = (offer: AllowOfferV2): Conversation.Trade.IAllowOffer => ({
  maxRefundAmount: toNumberValue(offer.maxRefundAmount),
  returnType: mapReturnType(offer.returnType),
  refundAmountAdjustable: Boolean(offer.refundAmountAdjustable),
});

const toV1ReturnStatus = (status: string) => {
  const map = {
    RETURN_OR_REFUND_REQUEST_PENDING: '1',
    REFUND_OR_RETURN_REQUEST_REJECT: '2',
    AWAITING_BUYER_SHIP: '3',
    BUYER_SHIPPED_ITEM: '4',
    REJECT_RECEIVE_PACKAGE: '5',
    RETURN_OR_REFUND_REQUEST_SUCCESS: '50',
    RETURN_OR_REFUND_REQUEST_CANCEL: '99',
    RETURN_OR_REFUND_REQUEST_COMPLETE: '100',
  } as any;
  return map[status] || status;
};

function mapReturnLine(
  ret: Return,
  line: ReturnLine | undefined,
  item: Item,
): Conversation.Trade.RefundReason {
  return {
    image: ret.buyerImages ?? [],
    returnVideo: (ret.buyerVideos ?? []).map((video) => ({
      videoUrl: toStringValue(video.videoUrl),
      thumbnaiUrl: toStringValue(video.thumbnailUrl),
    })),
    returnCreateTime: toStringValue(
      ret.createTimeMs ? Number(ret.createTimeMs) / 1000 : ret.createTimeMs,
    ),
    reason: toStringValue(ret.reasonName),
    reassessedRequestReason: ret.reassessedReasonCode,
    textReason: toStringValue(ret.reasonText),
    textReasonTrans: {} as Conversation.Trade.Translation,
    returnSn: toStringValue(ret.externalReturnId),
    returnStatus: toV1ReturnStatus(toStringValue(ret.returnStatusOriginal ?? ret.status)),
    returnQuantity: toNumberValue(line?.quantity ?? item.quantity),
    operate: (ret.availableOperations ?? []) as Conversation.Trade.IReturnOperationType[],
    displayStatus: (ret.displayStatus ?? 'UNKNOWN') as Conversation.Trade.IDisplayStatus,
    refundAmount: toNumberValue(ret.refundAmount),
    latestOfferAmount: toNumberValue(ret.latestOfferAmount),
    offerDueDateMs: toStringValue(ret.offerAutoAcceptTimeMs),
    returnSellerDueDateMs: toStringValue(ret.returnSellerDeadlineTimeMs),
    dueDateMs: toStringValue(ret.handleDeadlineTimeMs ?? line?.handleDeadlineTimeMs),
    returnType: mapReturnType(ret.returnType),
    offerCounterLimit: toNumberValue(ret.offerCounterLimit),
    sellerEvidenceDeadlineMs: toStringValue(ret.sellerEvidenceDeadlineTimeMs),
    allowOffer: (ret.allowOffers ?? []).map(mapAllowOffer),
    sellerCompensationDueDateMs: toStringValue(ret.sellerCompensationDeadlineTimeMs),
    returnOrderItemId: toStringValue(line?.externalReturnLineId || line?.orderLineId),
    returnHistory: (ret.histories ?? []).map((history: any) => ({
      ...history,
      buyerImage: history.buyerImages,
      reason: history.reasonName,
      updateTime: toNumberValue(history.updateTimeMs),
    })),
    returnTrackingNumber: ret.returnTrackingNumber,
    returnShipmentId: toStringValue(ret.returnShipmentId),
    reasonName: ret.reasonName,
    preDispute: ret.preDispute
      ? {
          failReasonCode: toNumberValue(ret.preDispute.failReasonCode),
          content: toStringValue(ret.preDispute.content),
        }
      : undefined,
    subOrderLineItemIds: line?.subOrderLineIds,
  };
}

function matchReturnLine(ret: Return, item: Item) {
  const itemSubLineIds = item.subItems?.map((subItem) => subItem.subOrderLineId).filter(Boolean) as
    | string[]
    | undefined;

  return ret.returnLines?.find(
    (line) =>
      (line.orderLineId && item.orderLineIds?.includes(line.orderLineId)) ||
      intersects(line.subOrderLineIds, itemSubLineIds),
  );
}

function findItemReturnLines(item: Item, returns: Return[]) {
  const itemSubLineIds = item.subItems?.map((subItem) => subItem.subOrderLineId).filter(Boolean) as
    | string[]
    | undefined;

  return returns.filter(
    (ret) =>
      intersects(ret.orderLineIds, item.orderLineIds) ||
      intersects(ret.subOrderLineIds, itemSubLineIds) ||
      Boolean(ret.externalReturnId && item.externalReturnIds?.includes(ret.externalReturnId)) ||
      ret.returnLines?.some(
        (line) =>
          (line.orderLineId && item.orderLineIds?.includes(line.orderLineId)) ||
          intersects(line.subOrderLineIds, itemSubLineIds),
      ),
  );
}

function mapComment(comment: ItemComment): Conversation.Trade.Comment {
  return {
    imageUrl: comment.imageUrls ?? [],
    videoUrl: comment.videoUrls ?? [],
    buyerComment: toStringValue(comment.buyerComment),
    transBuyerComment: {} as Conversation.Trade.Translation,
    rawRatings: {
      ratingStar: toNumberValue(comment.rating?.ratingStar),
      sellerRating: toNumberValue(comment.rating?.sellerRating),
      productRating: toNumberValue(comment.rating?.productRating),
      logisticsRating: toNumberValue(comment.rating?.logisticsRating),
    },
    commentId: toStringValue(comment.commentId),
    updateTime: toStringValue(comment.updateTimeMs),
  };
}

function findItemPackage(item: Item, packages: Package[]) {
  return packages.find(
    (pkg) =>
      intersects(pkg.orderLineIds, item.orderLineIds) ||
      intersects(
        pkg.subOrderLineIds,
        item.subItems?.map((subItem) => subItem.subOrderLineId).filter(Boolean) as string[],
      ),
  );
}

function mapItem(
  item: Item,
  returns: Return[],
  comments: ItemComment[],
  packages: Package[],
  erp?: ErpSection,
): Conversation.Trade.Product {
  const matchedReturns = findItemReturnLines(item, returns);
  const matchedComments = comments.filter((comment) =>
    intersects(comment.orderLineIds, item.orderLineIds),
  );
  const itemPackage = findItemPackage(item, packages);
  const isPreOrder = hasTag(item.tags, 'ITEM_PRE_ORDER');

  return {
    externalSkuId: toStringValue(item.externalSkuId),
    itemId: toStringValue(item.externalItemId),
    itemName: toStringValue(item.itemName),
    itemSku: toStringValue(item.itemSku),
    shopSku: item.shopSku,
    sellerSku: toStringValue(item.sellerSku),
    externalItemId: toStringValue(item.externalItemId),
    sku: toStringValue(item.skuName),
    skuName: toStringValue(item.skuName),
    quantity: toNumberValue(item.quantity),
    weight: toNumberValue(item.weight),
    itemPic: toStringValue(item.itemPic),
    discountedPrice: toNumberValue(item.discountedPrice),
    unitPrice: toNumberValue(item.unitPrice),
    isRefund: matchedReturns.length > 0 || hasTag(item.tags, 'ITEM_REFUNDED'),
    itemComments: matchedComments.map(mapComment),
    refundReason: matchedReturns.map((ret) => mapReturnLine(ret, matchReturnLine(ret, item), item)),
    skuUrl: item.itemUrl,
    itemUrl: item.itemUrl,
    isPreOrder,
    preOrderStatus: isPreOrder ? 'ALL_PRE_ORDER' : 'NOT_PRE_ORDER',
    promotionType: item.promotionType as Conversation.Trade.ProductPromotionType,
    promotionId: toStringValue(item.promotionId),
    promotionRole: item.promotionRole as Conversation.Trade.ProductPromotionRole,
    hit: hasTag(item.tags, 'ITEM_SEARCH_HIT'),
    freeGift: hasTag(item.tags, 'ITEM_FREE_GIFT'),
    goodsPrepareTime: toNumberValue(item.prepareTimeDays),
    subOrderId: item.orderLineIds?.[0],
    exchangeInfo: erp?.exchangeRecords?.map((record) => ({
      exchangedSku: toStringValue(record.exchangedSku),
      exchangedQuantity: toStringValue(record.exchangedQuantity),
    })),
    canExchangeInfo: (erp?.availableExchanges ?? []).map((option) => ({
      exchangedSku: toStringValue(option.exchangedSku),
      exchangedQuantity: toNumberValue(option.exchangedQuantity),
      commodityCost: toStringValue(option.commodityCost),
      prodUnitCost: toStringValue(option.prodUnitCost),
      isSale: Boolean(option.onSale),
      outStockFlag: Boolean(option.outOfStock),
    })),
    digitalInfo: item.digitalInfo,
    warehouse: item.warehouse,
    orderLineItemIds: item.orderLineIds,
    combinedListingSkus: item.subItems?.map((subItem) => ({
      productId: toStringValue(subItem.externalItemId),
      skuId: toStringValue(subItem.externalSkuId),
      sellerSku: toStringValue(subItem.sellerSku),
      skuCount: toNumberValue(subItem.quantity),
      title: toStringValue(subItem.itemName),
      image: toStringValue(subItem.itemPic),
      skuName: toStringValue(subItem.skuName),
      itemUrl: toStringValue(subItem.itemUrl),
      packageId: toStringValue(subItem.packageId),
      subOrderLineItemId: subItem.subOrderLineId ? [subItem.subOrderLineId] : [],
    })),
    packageId: toStringValue(itemPackage?.packageId),
  };
}

function mapBuyerPaid(buyerPaid?: BuyerPaid): Conversation.Trade.Vo['buyerPayment'] {
  return {
    itemPrice: toStringValue(buyerPaid?.itemSubtotal?.total),
    buyerPaidShippingFee: toStringValue(buyerPaid?.shippingFee?.total),
    voucherFromPlatform: toStringValue(buyerPaid?.itemSubtotal?.platformDiscount),
    voucherFromSeller: toStringValue(buyerPaid?.itemSubtotal?.sellerDiscount),
    coins: toStringValue(buyerPaid?.coinsDiscount),
    creditCardPromotion: toStringValue(buyerPaid?.creditCardDiscount),
    crossBorderTax: toStringValue(buyerPaid?.importTax),
    buyerTransactionFee: toStringValue(buyerPaid?.buyerTransactionFee),
    buyerTotalAmount: toStringValue(buyerPaid?.buyerPaidTotal),
    itemPriceAfterDiscount: toStringValue(buyerPaid?.itemSubtotal?.total),
    originalShippingFee: toStringValue(buyerPaid?.shippingFee?.originalAmount),
    shippingFeeSellerDiscount: toStringValue(buyerPaid?.shippingFee?.sellerDiscount),
    shippingFeePlatformDiscount: toStringValue(buyerPaid?.shippingFee?.platformDiscount),
    taxes: toStringValue(buyerPaid?.taxAmount),
    smallOrderFee: toStringValue(buyerPaid?.minimumSpendSurcharge),
    productProtection: toStringValue(buyerPaid?.productProtectionFee),
    serviceFee: toStringValue(buyerPaid?.serviceFee),
    icmsTax: toStringValue(buyerPaid?.icmsTax),
    otherTaxes: toStringValue(buyerPaid?.otherTaxes),
    shippingFeeSst: toStringValue(buyerPaid?.shippingFee?.salesServiceTax),
  };
}

function mapTradeIncome(sellerIncome?: SellerIncome): Conversation.Trade.TradeIncome {
  const itemAmount = sellerIncome?.itemAmount;
  const shippingAmount = sellerIncome?.shippingAmount;
  const discountAmount = sellerIncome?.discountAmount;
  const feesAndCharges = sellerIncome?.feesAndCharges;

  return {
    itemPrice: toStringValue(itemAmount?.total),
    originItemPrice: toStringValue(itemAmount?.originalAmount),
    refundItemPrice: toStringValue(itemAmount?.refundedAmount),
    shippingFee: toStringValue(shippingAmount?.total),
    buyerPaidShippingFee: toStringValue(shippingAmount?.buyerPaid),
    actualShippingFee: toStringValue(shippingAmount?.carrierCharged),
    refundShippingFee: toStringValue(shippingAmount?.refunded),
    shopeeShippingRebate: toStringValue(shippingAmount?.platformRebate),
    shippingFeeDiscountFrom3pl: toStringValue(shippingAmount?.carrierDiscount),
    discountFee: toStringValue(discountAmount?.total),
    voucherFromSeller: toStringValue(discountAmount?.sellerVoucher),
    shopeeDiscount: toStringValue(discountAmount?.platformSubsidy),
    chargesFee: toStringValue(feesAndCharges?.total),
    commissionFee: toStringValue(feesAndCharges?.commission),
    commissionFeeReturn: toStringValue(feesAndCharges?.commissionRefunded),
    serviceFee: toStringValue(feesAndCharges?.serviceFee),
    sellerTransactionFee: toStringValue(feesAndCharges?.transactionFee),
    escrowAmount: toStringValue(sellerIncome?.receivableAmount),
    vat: toStringValue(feesAndCharges?.vat),
    crossBorderTax: toStringValue(feesAndCharges?.importDuty),
    orderAmount: toStringValue(itemAmount?.salesAmount),
    salesTaxOnLvg: toStringValue(feesAndCharges?.lowValueGoodsSalesTax),
    tradeAdjustment: (sellerIncome?.adjustments ?? []).map((adjustment) => ({
      adjustmentReason: toStringValue(adjustment.reason),
      adjustmentTimeMs: toStringValue(adjustment.adjustedTimeMs),
      amount: toStringValue(adjustment.amount),
    })),
    totalAdjustment: toStringValue(sellerIncome?.totalAdjustment),
    finalAmount: toStringValue(sellerIncome?.settledAmount),
    cancelPrice: toStringValue(itemAmount?.cancelledAmount),
  };
}

/** V2 订单 -> V1 订单视图模型 */
export function toV1Trade(trade: TradeV2Order, fallbackPlatform?: string): Conversation.Trade.Vo {
  const base = trade.base ?? {};
  const items = trade.items?.items ?? [];
  const packages = trade.packages?.packages ?? [];
  const returns = trade.reverse?.returns ?? [];
  const cancels = trade.reverse?.cancels ?? [];
  const comments = trade.comments?.comments ?? [];
  const erp = trade.erp;
  const remark = trade.remark;
  const receiver = trade.receiver;
  const buyerPaid = trade.payment?.buyerPaid;
  const sellerIncome = trade.payment?.sellerIncome;
  const tags = base.tags ?? [];
  const platform = toV1Platform(base.platform, fallbackPlatform);
  const primaryCancel = cancels[0];
  const primaryCancelOperator = primaryCancel?.operationLogs?.[0]?.operator;
  const generatedOrders = [
    ...(getTagRef(tags, 'GENERATED_EXCHANGE')
      ? [{ orderId: getTagRef(tags, 'GENERATED_EXCHANGE')!, orderTag: 'EXCHANGE_ORDER' as const }]
      : []),
    ...(getTagRef(tags, 'GENERATED_REPLACEMENT')
      ? [
          {
            orderId: getTagRef(tags, 'GENERATED_REPLACEMENT')!,
            orderTag: 'REPLACEMENT_ORDER' as const,
          },
        ]
      : []),
  ];

  const hasErpDetail = Boolean(
    erp &&
      (erp.erpTradeStatus ||
        erp.processStatus ||
        erp.preWeight ||
        erp.realWeight ||
        erp.logisticsCompanyPriceWeight ||
        erp.oaNoteContent ||
        erp.erpOrderTags),
  );

  return {
    internalTradeId: toStringValue(base.internalTradeId),
    externalTradeId: toStringValue(base.externalTradeId),
    invoiceNumber: toStringValue(base.invoiceNumber),
    platform,
    tradeStatus: (base.sidebarStatus ?? 'UNKNOWN_ORDER_STATUS') as Conversation.Trade.Status,
    originStatus: toStringValue(base.statusesOriginal?.[0]) as Conversation.Trade.OriginStatus,
    operationUserName: primaryCancelOperator?.userName,
    operationTime: toStringValue(primaryCancelOperator?.operateTimeMs),
    internalBuyerId: toNumberValue(base.internalBuyerId),
    externalBuyerId: toStringValue(base.externalBuyerId),
    buyerNick: toStringValue(base.buyerNick),
    externalShopId: toStringValue(base.externalStoreId),
    createdTimeInMs: toNumberValue(base.createTimeMs),
    modifiedTimeInMs: toNumberValue(base.updateTimeMs),
    payTimeInMs: toNumberValue(base.payTimeMs),
    payment: toNumberValue(buyerPaid?.buyerPaidTotal ?? base.totalAmount),
    buyerMemos: toStringValue(remark?.buyer?.content),
    buyerMemosTrans: {} as Conversation.Trade.Translation,
    sellerMemos: remark?.platform?.content,
    lechatSellerMemos: remark?.lechat?.content,
    currency: toStringValue(base.currency),
    unit: toStringValue(base.currencySymbol),
    items: items.map((item) => mapItem(item, returns, comments, packages, erp)),
    cancelType: toV1CancelType(
      primaryCancel?.cancelBy,
    ) as unknown as Conversation.Trade.Vo['cancelType'],
    cancelReason: toStringValue(primaryCancel?.cancelReasonOriginal || primaryCancel?.cancelReason),
    cancelReasonTrans: {} as Conversation.Trade.Translation,
    cancelTrade: cancels.map((cancel) => ({
      cancelStatus: cancel.cancelStatusOriginal === 'CANCELLATION_REQUEST_PENDING' ? '1' : '0',
      cancelTradeId: toStringValue(cancel.externalCancelId),
      operate: (cancel.availableOperations ?? []) as ('CONFIRM' | 'REFUSE')[],
    })) as Conversation.Trade.Vo['cancelTrade'],
    shippingCarrier: toStringValue(base.shippingCarrier),
    itemPrice: toStringValue(buyerPaid?.itemSubtotal?.total),
    shippingFeeOriginal: toStringValue(buyerPaid?.shippingFee?.originalAmount),
    shippingFeeDiscount: toStringValue(buyerPaid?.shippingFee?.totalDiscount),
    shippingFeeActual: toStringValue(buyerPaid?.shippingFee?.total),
    sellerPromotion: toStringValue(buyerPaid?.itemSubtotal?.sellerDiscount),
    totalAmount: toStringValue(buyerPaid?.buyerPaidTotal ?? base.totalAmount),
    voucherPlatform: toStringValue(buyerPaid?.itemSubtotal?.platformDiscount),
    voucherSeller: toStringValue(buyerPaid?.itemSubtotal?.sellerDiscount),
    adjustAmount: toStringValue(sellerIncome?.totalAdjustment),
    cod: hasTag(tags, 'COD'),
    paymentMethod: toStringValue(base.paymentMethod),
    shipByDate: toStringValue(base.shipDeadlineTimeMs),
    pickupDoneTime: toStringValue(base.pickupDoneTimeMs),
    phone: toStringValue(receiver?.phone || receiver?.mobile),
    email: receiver?.email,
    address: toStringValue(receiver?.address),
    country: toStringValue(receiver?.country),
    city: toStringValue(receiver?.city),
    buyerPayment: mapBuyerPaid(buyerPaid),
    tradeIncome: mapTradeIncome(sellerIncome),
    containsPreOrderItem:
      hasTag(tags, 'PRE_ORDER') || items.some((item) => hasTag(item.tags, 'ITEM_PRE_ORDER')),
    region: toStringValue(base.region) as General.UpperCaseRegion,
    storeName: toStringValue(base.storeName),
    storeAlias: toStringValue(base.storeAlias),
    storeType: toStringValue(base.storeType) as Conversation.Trade.IStoreType,
    fulfillmentFlag: toV1FulfillmentFlag(base.fulfilledBy),
    customTradeIncome: (sellerIncome?.customItems ?? []).map((item) => ({
      name: toStringValue(item.name),
      price: toStringValue(item.amount),
    })),
    sellerMemosOperationLog: remark?.logs?.map((log) => ({
      userName: toStringValue(log.operator?.userName),
      operateTimeMs: toStringValue(log.operator?.operateTimeMs),
      raw: JSON.stringify({ sellerMemos: toStringValue(log.after) }),
    })),
    erpDetail: hasErpDetail
      ? {
          logisticsCompanyPriceWeight: toStringValue(erp?.logisticsCompanyPriceWeight),
          oaNoteContent: toStringValue(erp?.oaNoteContent),
          orderTags1: toStringValue(erp?.erpOrderTags),
          preWeight: toStringValue(erp?.preWeight),
          processStatus: toStringValue(erp?.processStatus),
          realWeight: toStringValue(erp?.realWeight),
          erpTradeStatus: erp?.erpTradeStatus as Conversation.Trade.ErpDetail['erpTradeStatus'],
        }
      : undefined,
    reputation: hasTag(tags, 'AFFECTS_REPUTATION'),
    matchBooking: hasTag(tags, 'MATCH_BOOKING'),
    bookingId: toStringValue(getTagRef(tags, 'MATCH_BOOKING')),
    postCode: toStringValue(receiver?.postCode),
    buyerName: toStringValue(receiver?.name),
    taxIdType: toStringValue(receiver?.taxIdType),
    taxIdNumber: receiver?.taxIdNumber,
    // 仅 Shopee 超过三个月等平台备注不可编辑的场景，编辑位让给乐聊备注
    editSellerMemoType:
      platform === 'shopee' && remark?.platform?.editable === false ? 'LECHAT' : undefined,
    // [MERCADO] packageId 即 v1 的 packId
    packId: platform === 'mercado' ? packages[0]?.packageId : undefined,
    replacedOrderId: getTagRef(tags, 'FROM_REPLACEMENT'),
    exchangeSourceOrderId: toStringValue(getTagRef(tags, 'FROM_EXCHANGE')),
    generatedOrders: generatedOrders.length ? generatedOrders : undefined,
  };
}

function toSearchTradesV2Query(params: SearchParamsExtras): SearchTradesV2Query {
  return {
    internalStoreId: toNumberValue(params.internal_store_id),
    pageNo: toNumberValue(params.page_no) || 1,
    pageSize: toNumberValue(params.page_size) || 10,
    internalBuyerId: params.internal_buyer_id,
    externalBuyerId:
      params.external_buyer_id === undefined ? undefined : String(params.external_buyer_id),
    sidebarStatus: params.trade_status as SearchTradesV2Query['sidebarStatus'],
    internalTradeIds: splitIds(params.internal_trade_ids),
    externalTradeIds: splitIds(params.external_trade_ids),
    trackingNumber: params.trackingNumber,
    invoiceNumber: params.invoice_number,
    externalItemId: params.spuId ?? params.externalItemId,
    externalSkuId: params.sku ?? params.skuId ?? params.externalSkuId,
    sellerSku: params.sellerSku,
    itemName: params.itemName,
    // 与 V1 行为对齐：V1 接口恒定刷新
    refreshImmediately: true,
    filterCanceledReverse: params.filter_canceled_refund ?? true,
    // Sider 列表卡片需要展示金额、退单、备注等全部信息，统一请求全部 section
    include: ['ALL'],
  };
}

/** V2 单页上限 */
const V2_MAX_PAGE_SIZE = 50;
/** 兼容 V1 大分页调用（如 ToDo 一次拉 9999），最多聚合的页数 */
const V2_MAX_AGGREGATED_PAGES = 200;

/**
 * 以 V1 `TradeListResponse` 的形状调用 V2 接口。
 *
 * V2 单页上限 50，而部分存量调用会一次请求 999 / 9999 条（订单卡片关联、
 * ToDo 表单、数据看板等）。这里按 50 条一页串行聚合，对调用方保持 V1 语义。
 */
export async function tradeListV2AsV1(
  params: SearchParamsExtras,
  platform?: string,
  newCancelToken?: newCancelTokenFn,
  signal?: GenericAbortSignal,
): Promise<TradeListResponse> {
  const requestedPageNo = Math.max(1, toNumberValue(params.page_no) || 1);
  const requestedPageSize = Math.max(1, toNumberValue(params.page_size) || 10);
  const query = toSearchTradesV2Query(params);

  if (requestedPageSize <= V2_MAX_PAGE_SIZE) {
    const { page, trades = [] } = await tradeListV2(
      { ...query, pageNo: requestedPageNo, pageSize: requestedPageSize },
      newCancelToken,
      signal,
    );

    return {
      pageInfo: {
        pageNo: page?.pageNo ?? requestedPageNo,
        pageSize: page?.pageSize ?? requestedPageSize,
        totalCount: page?.totalCount ?? trades.length,
        hasNext: page?.hasNext,
      },
      tradeList: trades.map((trade) => toV1Trade(trade, platform)),
    };
  }

  const trades: TradeV2Order[] = [];
  let totalCount = 0;
  let hasNext = false;
  let pageNo = requestedPageNo;

  for (let fetchedPages = 0; fetchedPages < V2_MAX_AGGREGATED_PAGES; fetchedPages += 1) {
    const { page, trades: pageTrades = [] } = await tradeListV2(
      { ...query, pageNo, pageSize: V2_MAX_PAGE_SIZE },
      newCancelToken,
      signal,
    );

    totalCount = page?.totalCount ?? totalCount;
    hasNext = Boolean(page?.hasNext);
    trades.push(...pageTrades);

    if (!hasNext || pageTrades.length === 0 || trades.length >= requestedPageSize) {
      break;
    }

    pageNo += 1;
  }

  return {
    pageInfo: {
      pageNo: requestedPageNo,
      pageSize: requestedPageSize,
      totalCount: totalCount || trades.length,
      hasNext,
    },
    tradeList: trades.slice(0, requestedPageSize).map((trade) => toV1Trade(trade, platform)),
  };
}
